import React, { useState, useRef, useLayoutEffect, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '../icons';

/**
 * Menu di azioni per una riga di tabella. Un solo pulsante "Azioni" apre un
 * pannello con le voci contestuali, invece di allineare tre-quattro pulsanti in
 * cella — layout che sfondava la colonna e andava in scroll orizzontale a ogni
 * azione aggiunta. Scala a qualunque numero di voci senza toccare le larghezze.
 *
 * Il pannello vive in portal su document.body (come UserMenu/Select): la tabella
 * ha overflow, quindi un popover figlio verrebbe tagliato. La posizione è
 * calcolata da getBoundingClientRect e riallineata su scroll/resize.
 *
 * `items`: [{ label, icon, tone?: 'warn'|'danger', onClick, hidden?, disabled? }].
 */
export function RowMenu({ items, label = 'Azioni' }) {
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState(null);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const close = useCallback(() => setOpen(false), []);

  const visible = items.filter((item) => !item.hidden);

  useLayoutEffect(() => {
    if (!open) return;
    const update = () => {
      const el = triggerRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      // Pannello sotto il trigger, allineato al suo bordo destro.
      setRect({ top: r.bottom + 6, right: window.innerWidth - r.right });
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onPointer = (event) => {
      if (triggerRef.current?.contains(event.target)) return;
      if (panelRef.current?.contains(event.target)) return;
      close();
    };
    const onKey = (event) => { if (event.key === 'Escape') close(); };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, close]);

  if (visible.length === 0) return null;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={`btn btn-accent btn-sm row-menu-trigger ${open ? 'is-open' : ''}`}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <Icon name="settings" size={15} />
        {label}
        <Icon name="chevronDown" size={14} className="row-menu-caret" />
      </button>
      {open && rect && createPortal(
        <div
          ref={panelRef}
          className="user-menu-panel row-menu-panel"
          style={{ position: 'fixed', top: rect.top, right: rect.right }}
          role="menu"
        >
          <div className="user-menu-list">
            {visible.map((item, index) => (
              <button
                key={index}
                type="button"
                role="menuitem"
                className={`user-menu-item ${item.tone === 'danger' ? 'user-menu-item--danger' : ''} ${item.tone === 'warn' ? 'row-menu-item--warn' : ''}`}
                disabled={item.disabled}
                onClick={() => { setOpen(false); item.onClick(); }}
              >
                {item.icon && <Icon name={item.icon} size={15} />}
                {item.label}
              </button>
            ))}
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
